import { ChangeDetectorRef, Component, ComponentFactoryResolver, ComponentRef, HostListener, Inject, Injector, OnInit, TemplateRef, Type, ViewChild, ViewContainerRef } from "@angular/core";
import { Nullable } from "ng/src/app/shared/model/app.model";
import { PersistentDialogChildService, PersistentDialogService } from "ng/src/app/shared/persistent-dialog/persistent-dialog.service";
import { PersistentDialogController, PersistentDialogSettings } from "ng/src/app/shared/persistent-dialog/persistent-dialog.model";
import { Dialog } from "primeng/dialog";
import { LogBookRootScopeService } from "app/shared/model/app.model";
import { delay, filter, take, takeUntil } from "rxjs/operators";
import { PrimeNGHelpersService } from "ng/src/app/core/services/primeng-helpers.service";

@Component({
	selector: 'lb-dialog',
	template: `
		<p-dialog #dialog appendTo="body" [(visible)]="dialogVisible" [closeOnEscape]="false" [position]="$any(position)" [closable]="closeable" [header]="header" (onHide)="close()"
		          [resizable]="false" [responsive]="true" [draggable]="false" [modal]="true" [style]="{ width: width }">
			<div style="max-width: 100%; width: 100%; height: 100%">
				<ng-container [ngSwitch]="type">
					<ng-container *ngSwitchCase="'template'">
						<ng-container *ngTemplateOutlet="$any(currentToDisplay)"></ng-container>
					</ng-container>
					<ng-container *ngSwitchCase="'component'">
						<ng-container *ngComponentOutlet="$any(currentToDisplay)"></ng-container>
					</ng-container>
				</ng-container>
			</div>
		</p-dialog>
		<ng-container *ngFor="let controller of controlledDialogs">
			<p-dialog #cDialog [(visible)]="controller.visible" appendTo="body">
				<ng-container (container)="registerControlledContainer(cDialog, $event, controller)"></ng-container>
			</p-dialog>
		</ng-container>
	`
})
export class PersistentDialogComponent implements OnInit {
	@ViewChild('dialog') dialog!: Dialog;
	@ViewChild('container') container!: ViewContainerRef;
	
	currentToDisplay: Nullable<TemplateRef<any> | Type<any>> = null;
	settings: Nullable<PersistentDialogSettings> = null;
	dialogVisible = false;
	
	controlledDialogs: PersistentDialogController[] = [];
	
	get type() {
		return this.currentToDisplay instanceof TemplateRef ? 'template' : this.currentToDisplay instanceof Function ? 'component' : null;
	}
	
	get closeable() {
		return this.settings?.closeable ?? true;
	}
	
	get header() {
		return typeof this.settings?.header === 'string' ? this.settings.header : '';
	}
	
	get escapeToClose() {
		return this.settings?.escapeToClose ?? true;
	}
	
	get width() {
		return this.settings?.width ?? '750px';
	}
	
	get position() {
		return this.settings?.position;
	}
	
	constructor(
		private persistentService: PersistentDialogService,
		private changeDetectorRef: ChangeDetectorRef,
		private injector: Injector,
		private factoryResolver: ComponentFactoryResolver,
		@Inject('$rootScope')
		private $rootScope: LogBookRootScopeService
	) {}
	
	@HostListener('document:keydown.escape', ['$event'])
	onKeyUp(event: KeyboardEvent) {
		this.controlledDialogs.forEach(controller => controller.closeOnEscape && controller.close());
		
		const enabled = typeof this.escapeToClose === 'function' ? this.escapeToClose() : (this.escapeToClose ?? true);
		if (this.dialogVisible && enabled)
			this.close();
	}
	
	ngOnInit(): void {
		this.persistentService.templateSubject.subscribe(update => {
			if (update == null) {
				this.currentToDisplay = null;
				this.updateSettings(null);
			}
			else if (update instanceof PersistentDialogController) {
				update.register(this);
				this.controlledDialogs.push(update);
				return;
			}
			else if (update instanceof TemplateRef || 'template' in update) {
				if (update instanceof TemplateRef) {
					this.currentToDisplay = update;
					this.updateSettings(null);
				}
				else {
					this.currentToDisplay = update.template;
					this.updateSettings(update.settings);
				}
			}
			else if (update instanceof ComponentRef || 'component' in update) {
				if (update instanceof ComponentRef) {
					this.currentToDisplay = update;
					this.updateSettings(null);
				}
				else {
					this.currentToDisplay = update.component;
					this.updateSettings(update.settings);
				}
			}
			
			this.updateStyle();
			this.dialogVisible = !!this.currentToDisplay;
			this.changeDetectorRef.detectChanges();
		});
		
		LogBookRootScopeService.observeEvent(this.$rootScope, '$routeChangeStart')
			.pipe(
				delay(0), //Sets a delay to allow angular event listeners to finish processing to determine whether event is prevented
			)
			.subscribe(([event, current, previous]) => {
				if (current.$$route === previous?.$$route || event.defaultPrevented) return;
				this.controlledDialogs.forEach(controller => controller.closeOnNavigation && controller.close());
				this.close(true);
			});
		
		LogBookRootScopeService.observeEvent(this.$rootScope, '$routeUpdate')
			.pipe(
				delay(0)
			)
			.subscribe(([event]) => {
				if (event.defaultPrevented) return;
				this.controlledDialogs.forEach(controller => controller.closeOnNavigation && controller.close());
				this.close(true);
			});
		
		this.persistentService.closeSubject.subscribe(() => {
			this.close();
			this.controlledDialogs.forEach(controller => controller.respondToServiceClose && controller.close());
		})
	}
	
	updateSettings(settings: PersistentDialogSettings | null) {
		this.settings = settings;
		
		const headerRef = settings?.header instanceof TemplateRef ? settings.header : null;
		this.updateHeader(headerRef);
		this.updateFooter(settings?.footer ?? null);
		this.dialog.maximized = settings?.fullscreen ?? false;
	}
	
	updateHeader(templateRef: TemplateRef<any> | null) {
		this.dialog.headerTemplate = templateRef as any;
	}
	
	updateFooter(templateRef: TemplateRef<any> | null) {
		this.dialog.footerTemplate = templateRef as any;
	}
	
	updateStyle() {
		this.dialog.breakpoints = this.settings?.breakpoints as any;
		this.dialog.createStyle();
	}
	
	close(preventEvent = false) {
		if (!preventEvent && this.settings?.locationChangeOnClose && this.dialogVisible) {
			if (this.$rootScope.$emit('$locationChangeStart', window.location.href).defaultPrevented) {
				this.dialogVisible = true;
				return;
			}
		}
		try {
			this.settings?.onClose?.();
		}
		finally {
			this.dialogVisible = false;
			this.currentToDisplay = null;
			this.settings = null;
			this.changeDetectorRef.detectChanges();
		}
	}
	
	registerControlledContainer(dialog: Dialog, view: ViewContainerRef, controller: PersistentDialogController) {
		const injector = this.getChildDialogInjector();
		
		controller.onDialogCreated?.(dialog);
		
		const component = this.factoryResolver.resolveComponentFactory(controller.componentType).create(injector);
		controller.onComponentCreated?.(component);
		const viewRef = view.insert(component.hostView);
		viewRef.detectChanges();
		viewRef.onDestroy(() => controller.close());
		
		const childService = injector.get(PersistentDialogChildService);
		childService.controller = controller;
		
		childService.destroy$.pipe(
			take(1)
		)
			.subscribe(() => controller.close());
		
		
		PrimeNGHelpersService.onDialogContainerReady(dialog, () => {
			controller.onDialogVisible?.(dialog.container, dialog);
		})
	}
	
	private getChildDialogInjector() {
		return Injector.create({ providers: [{ provide: PersistentDialogChildService }], parent: this.injector });
	}
}